import { styled, Autocomplete, TextField, Box, Popper } from '@mui/material';
import { rem, Bold, border } from '@core-web/typography';
import { ScrollBarStyle } from '@core-web/ui';

export const AutocompleteWrapper = styled(Box)(({ theme: { palette } }) => ({
  display: 'flex',
  outline: 'none',
  width: '100%',
  '& > *': {
    outline: 'none',
    '&:focus-visible': {
      outline: 'none',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },

  '.MuiSvgIcon-root': {
    width: rem(16),
    height: rem(16),
  },

  '.MuiAutocomplete-root': {
    // minWidth: rem(226),
    backgroundColor: palette.colors.white,
    outline: 'none',
    width: '100%',
  },

  '.MuiPaper-root': {
    //padding: rem(8),
  },

  '.MuiAutocomplete-popper': {
    position: 'fixed !important',
    top: 0,
  },

  '.MuiAutocomplete-listbox': {
    maxHeight: rem(220),
    padding: `0 ${rem(4)}`,
    ...ScrollBarStyle(palette),
    li: {
      backgroundColor: 'transparent !important',
    },
  },

  '.MuiAutocomplete-option': {
    borderBottom: border(1, 'solid', palette.colors.black_25),
    padding: '0 !important',
  },

  '.Mui-focused': {
    backgroundColor: 'transparent !important',
  },
}));

export const TextWrapper = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'flex-start',
}));

export const MuiAutocomplete = styled(Autocomplete)(({ theme: { palette } }) => ({
  '.MuiAutocomplete-inputRoot': {
    height: rem(34),

    '&.Mui-disabled': {
      backgroundColor: palette.background.disabled,
      color: palette.colors.grey_300,
    },
  },

  '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
    paddingRight: `${rem(56)} !important`,
    outline: 'none',
  },
  '.MuiAutocomplete-input': {
    fontSize: rem(14),
    lineHeight: rem(18),
    fontWeight: Bold._500,
    outline: 'none',
  },
  '.MuiAutocomplete-endAdornment': {
    top: rem(14),
    svg: {
      fill: palette.colors.grey_500,
    },
  },
  '.MuiAutocomplete-popupIndicatorOpen': {
    top: `${rem(5)} !important`,
  },
  '.MuiAutocomplete-popupIndicator:hover': {
    backgroundColor: 'transparent !important',
  },

  '.MuiAutocomplete-clearIndicator': {
    top: rem(4),
  },
  '.MuiCircularProgress-root': {
    position: 'absolute',
    display: 'inline-block',
    right: rem(30),
  },

  '.MuiOutlinedInput-notchedOutline': {
    borderColor: palette.colors.grey_300,
  },

  '.MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${palette.colors.grey_300}`,
  },

  '&.withRequiredBorder .MuiOutlinedInput-notchedOutline': {
    borderColor: `${palette.colors.orange}`,
  },
  '&.withRequiredBorder .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${palette.colors.orange}`,
  },
  '&.hasValue .MuiOutlinedInput-notchedOutline': {
    borderColor: `${palette.colors.grey_300}`,
  },
  '&.hasValue .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${palette.colors.grey_300}`,
  },
  '&.hasError .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.error.main,
  },
  '&.hasError .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${palette.error.main}`,
  },
  '&.Mui-focused .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    borderColor: `${palette.colors.grey_300}`,
    borderWidth: rem(1),
  },
  '&.hideOptionWrapper + .MuiAutocomplete-popper': {
    display: 'none',
  },
  '&.hasError .MuiInputBase-input': {
    '::placeholder': {
      color: palette.error.main,
    },
  },
  '&.hasError .MuiFormControl-root': {
    backgroundColor: palette.error.bgLight,
  },
}));

export const MuiAutocompleteTextField = styled(TextField)(() => ({
  height: rem(34),
}));

export const MuiListItemContent = styled(Box)(({ theme: { palette } }) => ({
  fontSize: rem(12),
  fontWeight: Bold._500,
  lineHeight: rem(18),
  width: '100%',
  padding: rem(8),
  margin: `${rem(4)} 0`,
  '&:hover': {
    backgroundColor: palette.colors.grey_50,
  },
}));

interface AutoCompleteValueProps {
  size: number;
}

export const AutoCompleteValue = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<AutoCompleteValueProps>(({ theme: { palette }, size }) => ({
  marginLeft: rem(16),
  '.MuiInputBase-root': {
    width: rem(size),
    borderColor: palette.colors.grey_200,
    '.MuiOutlinedInput-notchedOutline': {
      borderColor: `${palette.colors.grey_200} !important`,

      '&:focused': {
        borderColor: `${palette.colors.grey_200} !important`,
      },
    },
  },

  '.Mui-readOnly': {
    textAlign: 'center',
  },
}));

export const ReadonlyBoxField = styled(TextField)(({ theme: { palette } }) => ({
  '.MuiInputBase-root': {
    input: {
      fontSize: rem(14),
      lineHeight: rem(20),
      border: 'none',
      textAlign: 'center',
      fontWeight: Bold._400,
      color: palette.colors.grey_500,
    },
  },
  '&.Mui-focused': {
    borderColor: palette.colors.grey_200,
    '&:hover': {
      borderColor: palette.colors.grey_200,
    },
    svg: {
      fill: palette.colors.orange,
    },
    ' .MuiInputAdornment-positionEnd': {
      button: {
        svg: {
          fill: palette.colors.orange,
        },
      },
    },
  },
}));

export const CheckBoxWrapper = styled(Box)(() => ({
  marginLeft: rem(8),
}));

// Custom Popper with added class
export const CustomPopper = styled(Popper)(({ theme: { palette } }) => ({
  '&.custom-popper-class': {
    position: 'fixed !important',
    top: 0,
    zIndex: 8999,

    '.MuiAutocomplete-paper': {
      border: border(1, 'solid', palette.colors.grey_200),
    },
    '.MuiAutocomplete-listbox': {
      maxHeight: rem(220),
      padding: `0 ${rem(4)}`,
      ...ScrollBarStyle(palette),
      li: {
        backgroundColor: 'transparent !important',
      },
    },

    '.MuiAutocomplete-option': {
      borderBottom: border(1, 'solid', palette.colors.black_25),
      padding: '0 !important',
    },

    '.Mui-focused': {
      backgroundColor: 'transparent !important',
    },
  },
}));

export const AddNewButtonWrapper = styled(Box)(({ theme: { palette } }) => ({
  padding: `${rem(8)} ${rem(8)}`,
  margin: `${rem(8)}${rem(3)} ${rem(1)}`,
  cursor: 'pointer',
  color: palette.colors.black_400,
  backgroundColor: palette.colors.grey_50,
  fontSize: rem(14),
  fontWeight: Bold._500,
  lineHeight: rem(18),
  textAlign: 'center',
  borderRadius: rem(4),
  border: `${rem(1)} solid ${palette.colors.grey_200}`,
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: palette.colors.grey_50,
    borderColor: palette.colors.grey_300,
  },

  '&:active': {
    backgroundColor: palette.colors.grey_100,
  },
}));

export const NoDataText = styled(Box)(({ theme: { palette } }) => ({
  padding: `${rem(1)}`,
  marginBottom: `${rem(4)}`,
  color: palette.colors.grey_500,
  fontSize: rem(14),
  fontWeight: Bold._400,
  lineHeight: rem(18),
  textAlign: 'center',
}));
